import { fill } from "@foretagsplatsen/core-helpers/array";
import { map } from "../../../core/helpers/object-util.js";
import CurrentPeriodForecastColumnTemplate from "../template/CurrentPeriodForecastColumnTemplate.js";
import ForecastBalanceSheet from "./reports/ForecastBalanceSheet.js";
import ForecastCashFlow from "./reports/ForecastCashFlow.js";
import ForecastIncomeStatement from "./reports/ForecastIncomeStatement.js";
import PredictedForecastColumnTemplate from "../template/PredictedForecastColumnTemplate.js";
import ReferenceForecastColumnTemplate from "../template/ReferenceForecastColumnTemplate.js";
import RelativePeriodTemplate from "../../reports/templates/RelativePeriodTemplate.js";
import TemplateInterpreter from "../../reports/TemplateInterpreter.js";

/**
 * This class is responsible for converting a forecast's report
 * template into a forecast's report. An interpretation context
 * (including, e.g., a period and a company) drives the
 * conversion. Most of the work is delegated to the
 * `TemplateInterpreter` class that is also used by the report editor.
 *
 * @param {ForecastTemplate} forecastTemplate - The forecast template
 * containing the report templates to convert
 * @param {ForecastTemplateInterpretationContext} forecastTemplateInterpretationContext - The data used to do the conversion
 */
export default class ForecastReportTemplateInterpreter {
	constructor({ forecastTemplate, forecastTemplateInterpretationContext }) {
		this._forecastTemplate = forecastTemplate;

		this._forecastTemplateInterpretationContext =
			forecastTemplateInterpretationContext;
	}

	interpretReportTemplates() {
		const reportTemplates = this._forecastTemplate.getReportTemplates();

		return map(reportTemplates, (value) =>
			this._interpretReportTemplate(value),
		);
	}

	_interpretReportTemplate(reportTemplate) {
		reportTemplate.setColumns([
			...this._createReferenceForecastColumnTemplates(),
			this._createCurrentPeriodColumnTemplate(),
			...this._createPredictedForecastColumnTemplates(),
		]);

		const reportTemplateInterpretationContext =
			this._forecastTemplateInterpretationContext.getReportTemplateInterpretationContext();

		const report = new TemplateInterpreter({
			context: reportTemplateInterpretationContext,
		}).interpret(reportTemplate);

		return this._makeForecastReport({ report });
	}

	_createReferenceForecastColumnTemplates() {
		const configuration = this._forecastTemplate.getConfiguration();

		const length = configuration.getNumberOfReferencePeriods() - 1;

		return fill({
			length,
			fn: (index) =>
				this._makeReferenceForecastColumnTemplate({
					shift: index - length,
				}),
		});
	}

	_makeReferenceForecastColumnTemplate(context) {
		return new ReferenceForecastColumnTemplate({
			periodTemplate: new RelativePeriodTemplate({
				periodType: "Month",
				shiftPeriodType: "Month",
				shift: context.shift,
			}),
		});
	}

	_createCurrentPeriodColumnTemplate() {
		const isFrozen = !!this._forecastTemplate.getFrozenCurrentPeriod();

		return new CurrentPeriodForecastColumnTemplate({
			periodTemplate: new RelativePeriodTemplate({
				periodType: "Month",
				shiftPeriodType: "Month",
				shift: 0,
			}),
			isFrozen,
		});
	}

	_createPredictedForecastColumnTemplates() {
		const configuration = this._forecastTemplate.getConfiguration();

		return fill({
			length: configuration.getNumberOfPredictedPeriods(),
			fn: (index) =>
				this._makePredictedForecastColumnTemplate({ shift: index + 1 }),
		});
	}

	_makePredictedForecastColumnTemplate(context) {
		return new PredictedForecastColumnTemplate({
			periodTemplate: new RelativePeriodTemplate({
				periodType: "Month",
				shiftPeriodType: "Month",
				shift: context.shift,
			}),
		});
	}

	_makeForecastReport({ report }) {
		const ForecastReportClass = [
			ForecastIncomeStatement,
			ForecastBalanceSheet,
			ForecastCashFlow,
		].find((klass) => klass.getType() === report.getId());

		const forecastReport = new ForecastReportClass({ report });
		forecastReport.setupDefaultAccountVisibility();

		return forecastReport;
	}

	static interpret(...spec) {
		const interpreter = new ForecastReportTemplateInterpreter(...spec);

		return interpreter.interpretReportTemplates();
	}
}
